import { useEffect, useMemo, useState } from "react";
import CalendarHeader from "src/Components/Calendar/CalendarHeader";
import {
  getStartOfWeek,
  getMajorityMonthOfWeek,
  getWeekNumberInMonth,
  getDaysOfWeekByYearMonthWeek,
} from "src/Common/common";
import TimeOffService from "src/Services/API/timeoff.service";
import UserFormPermissionService from "src/Services/API/userformpermission.service";
import UserService from "src/Services/API/user.service";
import { handleError } from "src/Common/function";
import OffChip from "./OffChip";
import MoreChip from "./MoreChip";
import { TimeOffLegend } from "./timeOffShared";
import TimeOffDetail from "src/Pages/TimeOff/TimeOffDetail";
import { toast } from "react-toastify";

// Builds a 6-row month grid (leading/trailing days from adjacent months
// greyed out), mirroring CalendarGrid's getCalendarDays.
const getCalendarDays = (year, month) => {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startingDay = firstDay.getDay();
  const endingDay = lastDay.getDay();
  const prevMonthLastDay = new Date(year, month, 0).getDate();

  const calendarData = [];
  let day = 1;

  for (let i = 0; i < 6; i++) {
    const week = [];

    for (let j = 0; j < 7; j++) {
      if (i === 0 && j < startingDay) {
        week.push({ day: prevMonthLastDay - startingDay + j + 1, isCurrentMonth: false });
      } else if (day > daysInMonth) {
        if (endingDay === 6) continue;
        week.push({ day: day - daysInMonth, isCurrentMonth: false });
        day++;
      } else {
        week.push({ day, isCurrentMonth: true });
        day++;
      }
    }

    if (week.length > 0) calendarData.push(week);
    if (day > daysInMonth && (endingDay === 6 || week[6].day <= 7)) break;
  }

  return calendarData;
};

const isToday = (day, month, year, isCurrentMonth) => {
  const today = new Date();
  return isCurrentMonth && day === today.getDate() && month === today.getMonth() && year === today.getFullYear();
};

// Local midnight for a Y/M/D, so day comparisons ignore time-of-day.
const dayStart = (year, month, day) => new Date(year, month, day).getTime();

// "YYYY-MM-DD" in local time
const toIsoDay = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

function CalendarTimeOff({ renderHeader, branch }) {
  const today = new Date();
  const [currentDate, setCurrentDate] = useState(today);
  const [view, setView] = useState("Month");
  const [requests, setRequests] = useState([]);
  // Whether the currently logged-in user has Time Off display enabled (set by a
  // super admin in Team > Manage). When ON, all chips render rich; otherwise all
  // render the plain pill.
  const [richEnabled, setRichEnabled] = useState(false);
  // request whose details are being viewed in the side drawer, or null
  const [detailItem, setDetailItem] = useState(null);

  const weekStartDate = getStartOfWeek(currentDate);

  // Determine majority month/week for header and week number, matching the
  // Work Orders tab behaviour.
  const { year, month } =
    view === "Week"
      ? getMajorityMonthOfWeek(weekStartDate)
      : { year: currentDate.getFullYear(), month: currentDate.getMonth() };

  const monthName = new Date(year, month).toLocaleDateString("default", {
    month: "long",
  });

  const weekNumber = view === "Week" ? getWeekNumberInMonth(weekStartDate, month, year) : null;

  const weekdays = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
  const monthData = view === "Month" ? getCalendarDays(year, month) : [];
  const weekData = (view === "Week" ? getDaysOfWeekByYearMonthWeek(year, month, weekNumber) : []) || [];

  // Fetch time-off requests overlapping the visible month. We always pull the
  // whole month so both Month and Week views are covered.
  useEffect(() => {
    const start = new Date(year, month, 1);
    const end = new Date(year, month + 1, 0, 23, 59, 59, 999);
    const params = { startDate: toIsoDay(start), endDate: toIsoDay(end) };
    // when a branch is selected, only load time-off for technicians on that branch
    if (branch) params.branchId = branch;
    TimeOffService.getAll(params)
      .then((response) => {
        if (response.status === 200 || response.status === 201) {
          const data = response.data;
          const list = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
          // Show pending (0) and approved (1 = with PTO, 2 = without PTO); hide rejected (3).
          const visible = list.filter((req) => req.status === 0 || req.status === 1 || req.status === 2);
          setRequests(visible);
        }
      })
      .catch((error) => {
        handleError(error);
      });
  }, [month, year, branch]);

  // Fetch the logged-in user's "Time Off display" flag once. A super admin sets
  // this per user in Team > Manage; when ON, this user sees the rich,
  // colour-coded chips, otherwise the plain pills.
  useEffect(() => {
    const userId = UserService.getCurrentUser()?.id;
    if (userId == null) return;
    UserFormPermissionService.get(userId)
      .then((res) => setRichEnabled(!!res.data?.isTimeOffEnabled))
      .catch(() => setRichEnabled(false));
  }, []);

  // Returns the approved requests whose date range covers the given day.
  const requestsForDay = useMemo(() => {
    return (cellYear, cellMonth, cellDay) => {
      const ts = dayStart(cellYear, cellMonth, cellDay);
      return requests.filter((req) => {
        if (!req.startDate) return false;
        const s = new Date(req.startDate);
        const e = req.endDate ? new Date(req.endDate) : s;
        const startTs = dayStart(s.getFullYear(), s.getMonth(), s.getDate());
        const endTs = dayStart(e.getFullYear(), e.getMonth(), e.getDate());
        return ts >= startTs && ts <= endTs;
      });
    };
  }, [requests]);

  const handlePrev = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(view === "Month" ? 1 : newDate.getDate() - 7);
    if (view === "Month") {
      newDate.setMonth(currentDate.getMonth() - 1);
    }
    setCurrentDate(newDate);
  };

  const handleNext = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(view === "Month" ? 1 : newDate.getDate() + 7);
    if (view === "Month") {
      newDate.setMonth(currentDate.getMonth() + 1);
    }
    setCurrentDate(newDate);
  };

  // hoist the header into the tabbar row so it sits to the right of the tabs
  useEffect(() => {
    renderHeader?.(
      <CalendarHeader
        month={monthName}
        year={year}
        week={weekNumber}
        view={view}
        onPrev={handlePrev}
        onNext={handleNext}
        onView={setView}
      />,
    );
    return () => renderHeader?.(null);
  }, [monthName, year, view, weekNumber]);

  // Renders the OFF chips for a single day. Up to 2 named pills, then an
  // avatar-stack "+N" overflow that lists the rest in a popover.
  const renderOff = (dayRequests, cellMonth, cellDay) => {
    if (!dayRequests.length) return null;
    const MAX = 2;
    const named = dayRequests.slice(0, MAX);
    const overflow = dayRequests.slice(MAX);
    const label = `OFF — ${new Date(year, cellMonth, cellDay).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
    })}`;
    return (
      <div className="w-full mt-auto space-y-1">
        {named.map((req) => (
          <OffChip key={req.id} request={req} rich={richEnabled} onView={setDetailItem} />
        ))}
        {overflow.length > 0 && (
          <MoreChip requests={overflow} all={dayRequests} label={label} rich={richEnabled} onView={setDetailItem} />
        )}
      </div>
    );
  };

  return (
    <>
      <div className="py-6">
        {richEnabled && <TimeOffLegend />}
        <div className="grid grid-cols-7 text-left text-[14px] border border-r-0 border-[#BFC2CE] bg-white">
          {weekdays.map((day) => (
            <div
              key={day}
              className="py-2 border-r border-[#BFC2CE] p-[11px] text-subTitleColor font-OpenSansRegular text-[14px] not-italic font-normal leading-normal tracking-[-0.21px]"
            >
              {day}
            </div>
          ))}
        </div>

        {view === "Month" ? (
          monthData.map((week, i) => (
            <div key={i} className="grid grid-cols-7 border border-t-0 border-r-0 border-[#BFC2CE] bg-white">
              {week.map((data, j) => {
                const dayRequests = data.isCurrentMonth ? requestsForDay(year, month, data.day) : [];
                return (
                  <div
                    key={j}
                    className={`flex flex-col items-start justify-start p-2 min-h-[120px] border-r border-[#BFC2CE]
                      ${!data.isCurrentMonth ? "bg-[#F8F9FE]" : "bg-white"}`}
                  >
                    {data.day && (
                      <div
                        className={`mb-1 font-OpenSansRegular text-[16px] not-italic font-normal leading-normal tracking-[-0.24px] text-[#80869E] ${
                          !data.isCurrentMonth ? "text-[#404A6E]" : ""
                        } ${
                          isToday(data.day, month, year, data.isCurrentMonth) ? "text-[#0065FD] font-extrabold" : ""
                        }`}
                      >
                        {data.day}
                      </div>
                    )}
                    {renderOff(dayRequests, month, data.day)}
                  </div>
                );
              })}
            </div>
          ))
        ) : (
          <div className="grid grid-cols-7 border border-t-0 border-r-0 border-[#BFC2CE] bg-white">
            {weekData.map((data, i) => {
              const dayRequests = requestsForDay(year, month, data.day);
              return (
                <div
                  key={i}
                  className="flex flex-col items-start justify-start p-2 min-h-[120px] border-r border-[#BFC2CE] bg-white"
                >
                  <div
                    className={`mb-1 font-OpenSansRegular text-[16px] not-italic font-normal leading-normal tracking-[-0.24px] text-[#80869E] ${
                      isToday(data.day, month, year, true) ? "text-[#0065FD] font-extrabold" : ""
                    }`}
                  >
                    {data.day}
                  </div>
                  {renderOff(dayRequests, month, data.day)}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <TimeOffDetail
        item={detailItem}
        onClose={() => setDetailItem(null)}
        onApprove={(req, status) => {
          setRequests((prev) => prev.map((r) => (r.id === req.id ? { ...r, status } : r)));
          toast.success(status === 1 ? "Request approved with PTO" : "Request approved without PTO");
        }}
        onReject={(req) => {
          // rejected requests are hidden from the calendar, so drop it from the list
          setRequests((prev) => prev.filter((r) => r.id !== req.id));
          toast.success("Request rejected");
        }}
      />
    </>
  );
}

export default CalendarTimeOff;
